import { useState, useEffect } from 'react';
import { Settings, Zap, Flame, Coins } from 'lucide-react';
import { gameBridge } from '@/services/GameBridge';
import { profileStore } from '@/services/ProfileStore';
import { runState } from '@/services/RunState';
import { cn, formatXP, formatCoins, getStreakEmoji } from '@/lib/utils';
import { getSubjectByName } from '@/data/subjects';

interface HUDProps {
  onSettingsClick: () => void;
  onAchievementsClick: () => void;
}

export function HUD({ onSettingsClick, onAchievementsClick }: HUDProps) {
  const [subject, setSubject] = useState<string>('Hub');
  const [streak, setStreak] = useState(0);
  const [xp, setXp] = useState(0);
  const [coins, setCoins] = useState(0);
  const [progress, setProgress] = useState(0);
  const [remainingCards, setRemainingCards] = useState(0);
  const [isRunActive, setIsRunActive] = useState(false);
  const [runXP, setRunXP] = useState(0);
  const [runCoins, setRunCoins] = useState(0);

  // Update HUD data
  const updateHUD = () => {
    const runData = runState.getRunData();
    const profile = profileStore.getProfile();

    setIsRunActive(runData.active);

    if (runData.active && runData.currentSubject) {
      setSubject(runData.currentSubject);
      setStreak(runData.streak);
      setRunXP(runData.xpEarned);
      setRunCoins(runData.coinsEarned);
      setProgress(runState.getProgress());
      setRemainingCards(runState.getRemainingCards());
    } else {
      setSubject('Hub');
      setStreak(0);
      setRunXP(0);
      setRunCoins(0);
      setProgress(0);
      setRemainingCards(0);
    }

    setXp(profile.totalXP);
    setCoins(profile.totalCoins);
  };

  // Subscribe to game events
  useEffect(() => {
    // Initial update
    updateHUD();

    // Subscribe to relevant events
    const unsubscribers = [
      gameBridge.subscribe('scene-changed', updateHUD),
      gameBridge.subscribe('answer-submitted', updateHUD),
      gameBridge.subscribe('run-started', updateHUD),
      gameBridge.subscribe('run-completed', updateHUD),
      gameBridge.subscribe('xp-gained', updateHUD),
      gameBridge.subscribe('coins-gained', updateHUD),
      gameBridge.subscribe('streak-changed', updateHUD),
    ];

    // Poll for updates (fallback)
    const interval = setInterval(updateHUD, 500);

    return () => {
      unsubscribers.forEach(unsub => unsub());
      clearInterval(interval);
    };
  }, []);

  const subjectInfo = getSubjectByName(subject);
  const SubjectIcon = subjectInfo?.icon;
  const subjectColor = subjectInfo?.color || '#4488ff';
  const shortName = subjectInfo?.shortName || subject;

  return (
    <div className="hud-bar top px-4 py-3">
      <div className="flex items-center justify-between max-w-[640px] mx-auto">
        {/* Left side - Subject and Streak */}
        <div className="flex items-center gap-4">
          {/* Subject */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary/40 rounded-lg border border-border/40 transition-all hover:bg-secondary/60">
            {SubjectIcon && (
              <SubjectIcon
                className="w-4 h-4 transition-transform hover:scale-110"
                style={{ color: subjectColor }}
              />
            )}
            <span
              className="font-semibold text-sm tracking-wide"
              style={{ color: subjectColor }}
            >
              {shortName}
            </span>
          </div>

          {/* Streak */}
          <div className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all",
            streak >= 5 
              ? "streak-fire bg-game-gold/10 border-game-gold/30" 
              : streak > 0 
                ? "bg-orange-500/10 border-orange-500/30" 
                : "bg-secondary/40 border-border/40"
          )}>
            {streak >= 5 ? (
              <Flame className="w-4 h-4 text-game-gold animate-bounce-subtle" />
            ) : (
              <Zap className="w-4 h-4 text-muted-foreground" />
            )}
            <span className={cn(
              "text-sm font-bold",
              streak >= 5 ? "text-game-gold" : streak > 0 ? "text-orange-400" : "text-muted-foreground"
            )}>
              {streak}x {getStreakEmoji(streak)}
            </span>
          </div>
        </div>

        {/* Center - XP and Progress */}
        <div className="flex items-center gap-4">
          {/* XP */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 rounded-lg border border-blue-500/30">
            <span className="text-xs font-medium text-blue-300/80">XP</span>
            <span className="text-sm font-bold text-blue-400 transition-all">
              {isRunActive ? `+${formatXP(runXP)}` : formatXP(xp)}
            </span>
          </div>

          {/* Progress (only during run) */}
          {isRunActive && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary/40 rounded-lg border border-border/40">
              <span className="text-xs font-medium text-muted-foreground">{progress}%</span>
              <span className="text-xs text-muted-foreground/70">
                ({remainingCards} left)
              </span>
            </div>
          )}
        </div>

        {/* Right side - Coins and Settings */}
        <div className="flex items-center gap-3">
          {/* Coins */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
            <Coins className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-bold text-yellow-400">
              {isRunActive ? `+${formatCoins(runCoins)}` : formatCoins(coins)}
            </span>
          </div>

          {/* Achievements button */}
          <button
            onClick={onAchievementsClick}
            className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary border border-border/40 hover:border-primary/50 transition-all hover:scale-105 text-base"
            aria-label="Achievements"
          >
            ⚖️
          </button>

          {/* Settings button */}
          <button
            onClick={onSettingsClick}
            className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary border border-border/40 hover:border-primary/50 transition-all hover:scale-105"
            aria-label="Settings"
          >
            <Settings className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    </div>
  );
}
